import * as React from "react";
import { Mail } from "lucide-react";
import { Avatar } from "../../../Avatar";
import { ListViewCard } from "../ListViewCard";
import {
  normalizeAvatarUrl,
  formatRoleLabel,
  formatInviteExpiry,
} from "../DeckCard/logic/formatters";
import type { ListViewCardBadge, ListViewCardLayout, ListViewCardVariant, ListViewCTAGroup } from "../ListViewCard";

export interface ListViewOrgMemberCardRoleOption {
  id: string;
  name: string;
  color?: string | null;
}

export interface ListViewOrgMemberCardMember {
  id: string;
  kind: "active" | "pending" | "invited";
  displayName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  roleLabel?: string | null;
  roleColor?: string | null;
  inviteExpiresAt?: string | null;
  deletedAt?: string | null;
}

export interface ListViewOrgMemberCardProps {
  layout: ListViewCardLayout;
  member: ListViewOrgMemberCardMember;
  isOrgAdmin?: boolean;
  roleOptions?: ListViewOrgMemberCardRoleOption[];
  actionLoading?: boolean;
  roleDropdownDisabled?: boolean;
  onApprove?: () => void;
  onDeny?: () => void;
  onRoleChangeRequest?: (roleId: string) => void;
  onClick?: () => void;
  footerText?: string;
  animatedHighlightOverride?: boolean;
  ctaGroupsOverride?: ListViewCTAGroup[];
}

export function ListViewOrgMemberCard({
  layout,
  member,
  isOrgAdmin,
  roleOptions = [],
  actionLoading,
  roleDropdownDisabled,
  onApprove,
  onDeny,
  onRoleChangeRequest,
  onClick,
  footerText,
  animatedHighlightOverride,
  ctaGroupsOverride,
}: ListViewOrgMemberCardProps) {
  const isInvited = member.kind === "invited";
  const isPending = member.kind === "pending";

  const image = isInvited ? (
    <Mail size={layout === "deck" ? 40 : 28} style={{ color: "#9CA3AF" }} />
  ) : (
    <Avatar
      email={member.email}
      firstName={member.firstName ?? ""}
      lastName={member.lastName ?? ""}
      image={normalizeAvatarUrl(member.avatarUrl) || undefined}
      size={layout === "deck" ? 100 : 72}
    />
  );

  const title = isInvited ? member.email : member.displayName;
  const subtitle = isInvited
    ? member.inviteExpiresAt
      ? formatInviteExpiry(member.inviteExpiresAt)
      : undefined
    : member.email;

  const statusBadge = member.deletedAt
    ? { id: "status", label: "Disabled", variant: "disabled" as const }
    : isPending
    ? { id: "status", label: "Pending", variant: "pending" as const }
    : isInvited
    ? { id: "status", label: "Invited", variant: "invited" as const }
    : { id: "status", label: "Active", variant: "active" as const };

  const variant: ListViewCardVariant = isPending ? "attention" : isInvited ? "static" : "default";

  const showRoleDropdown =
    member.kind === "active" && isOrgAdmin && roleOptions.length > 0 && Boolean(onRoleChangeRequest);

  const showApproveDeny = isPending && isOrgAdmin && Boolean(onApprove) && Boolean(onDeny);

  // Role badge: clickable with dropdown if admin + has options, static chip otherwise
  const roleDisplay =
    member.roleLabel?.trim() ||
    (member.roleName ? formatRoleLabel(member.roleName) : null);
  const roleBadge: ListViewCardBadge | null = roleDisplay
    ? {
        id: "role",
        label: roleDisplay,
        color: member.roleColor ?? undefined,
        actions: showRoleDropdown
          ? roleOptions.map((r) => ({
              id: r.id,
              label: formatRoleLabel(r.name),
              color: r.color ?? undefined,
              disabled: r.id === member.roleId || actionLoading || roleDropdownDisabled,
              onClick: () => onRoleChangeRequest!(r.id),
            }))
          : undefined,
      }
    : null;

  const ctaGroups: ListViewCTAGroup[] | undefined = showApproveDeny
    ? [{
        id: "approve-deny",
        text: "Membership requested",
        buttons: [
          { id: "approve", label: "Approve", variant: "primary" as const, disabled: actionLoading, onClick: onApprove! },
          { id: "deny", label: "Deny", variant: "secondary" as const, disabled: actionLoading, onClick: onDeny! },
        ],
      }]
    : undefined;

  const badges: ListViewCardBadge[] = [statusBadge, ...(roleBadge ? [roleBadge] : [])];

  return (
    <ListViewCard
      id={member.id}
      layout={layout}
      imageFormat="avatar"
      image={image}
      title={title}
      subtitle={subtitle}
      badges={badges}
      footerText={footerText}
      onClick={!isInvited ? onClick : undefined}
      actionsDisabled={actionLoading}
      variant={variant}
      ctaGroups={ctaGroupsOverride ?? ctaGroups}
      animatedHighlight={animatedHighlightOverride ?? isPending}
    />
  );
}
